/**
 * Centralized GlowScore™ — secondary skin / image-quality index (not primary acne truth).
 *
 * Deterministic from brightness, resolution, face counts, and image hashes.
 * Same inputs always produce the same score. Acne severity should be driven by
 * YOLO + hybrid rules in `scanService` when available; this score is a baseline /
 * quality signal that complements vision models.
 *
 * Formula Documentation:
 * - Base score: 65 (neutral/healthy baseline)
 * - pH estimation: Based on brightness and image characteristics (4.5-6.5 range)
 * - Texture analysis: Based on resolution and image quality
 * - Face detection: Bonus for proper face detection (one face per photo)
 * - Quality indicators: Penalties for warnings (low light, multiple faces, etc.)
 * - Comprehensive analysis bonus: Extra points for 3-angle analysis
 * - pH-based adjustments: Score adjustments based on estimated pH
 *
 * Final score range: 35-98 (realistic distribution)
 */

import type { PhotoMetadata } from './photoProcessing';

/** Optional Rekognition-validated face counts (prefer over browser FaceDetector). */
export interface RekognitionGlowFaceCounts {
  /** Faces counted for the front photo (typically 1 when validation passed). */
  front?: number;
  leftSide?: number;
  rightSide?: number;
}

export interface GlowScoreInputs {
  frontMeta?: PhotoMetadata;
  frontWarnings?: string[];
  leftSideMeta?: PhotoMetadata;
  leftSideWarnings?: string[];
  rightSideMeta?: PhotoMetadata;
  rightSideWarnings?: string[];
  /** When set for an angle, overrides `PhotoMetadata.facesDetected` for GlowScore face logic. */
  rekognitionFaceCounts?: RekognitionGlowFaceCounts;
}

/** Prefer Rekognition count when provided; else browser FaceDetector count if present. */
function effectiveFaceCountForGlowScore(
  meta: PhotoMetadata | undefined,
  rekognitionCount: number | undefined,
): number | undefined {
  if (typeof rekognitionCount === 'number') return rekognitionCount;
  const fd = meta?.facesDetected;
  return typeof fd === 'number' ? fd : undefined;
}

export interface GlowScoreResult {
  score: number;
  estimatedPH: number;
  confidenceLevel: 'low' | 'medium' | 'high';
  confidenceScore: number;
  factors: {
    photoCount: number;
    totalFaces: number;
    qualityWarnings: number;
    avgBrightness?: number;
    avgResolution?: number;
  };
}

/**
 * Deterministic hash function for consistent seed-based calculations
 * Same seed always produces same result
 */
function deterministicHash(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Deterministic random number generator
 * Same seed always produces same value in the given range
 */
function deterministicRandom(seed: string, min: number = 0, max: number = 1): number {
  const hash = deterministicHash(seed);
  const normalized = hash / 2147483647; // Max 32-bit int
  return min + (normalized * (max - min));
}

/**
 * Clamp a number between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Calculate confidence score based on analysis quality
 */
function calculateConfidence(
  photoCount: number,
  totalFaces: number,
  qualityWarnings: number,
  avgBrightness?: number,
  avgResolution?: number
): { level: 'low' | 'medium' | 'high'; score: number } {
  let confidenceScore = 0;

  // Photo count (max 30 points)
  if (photoCount === 3) {
    confidenceScore += 30; // All three angles
  } else if (photoCount === 2) {
    confidenceScore += 20; // Two angles
  } else {
    confidenceScore += 10; // One angle
  }

  // Face detection (max 25 points)
  if (totalFaces === photoCount && photoCount >= 2) {
    confidenceScore += 25; // Perfect detection
  } else if (totalFaces > 0) {
    confidenceScore += 15; // Partial detection
  } else {
    confidenceScore += 0; // No faces detected
  }

  // Image quality (max 25 points)
  if (qualityWarnings === 0) {
    confidenceScore += 25;
  } else if (qualityWarnings <= 2) {
    confidenceScore += 15;
  } else {
    confidenceScore += 5;
  }

  // Brightness quality (max 10 points)
  if (avgBrightness !== undefined) {
    if (avgBrightness >= 70 && avgBrightness <= 120) {
      confidenceScore += 10; // Optimal brightness
    } else if (avgBrightness >= 50 && avgBrightness < 70) {
      confidenceScore += 5; // Acceptable brightness
    } else {
      confidenceScore += 2; // Poor brightness
    }
  }

  // Resolution quality (max 10 points)
  if (avgResolution !== undefined) {
    if (avgResolution >= 1500000) {
      confidenceScore += 10; // High resolution
    } else if (avgResolution >= 800000) {
      confidenceScore += 7; // Medium resolution
    } else {
      confidenceScore += 3; // Low resolution
    }
  }

  // Determine level
  let level: 'low' | 'medium' | 'high';
  if (confidenceScore >= 70) {
    level = 'high';
  } else if (confidenceScore >= 40) {
    level = 'medium';
  } else {
    level = 'low';
  }

  return { level, score: confidenceScore };
}

/**
 * Calculate GlowScore™ (Skin Health Index) from photo metadata
 * 
 * This function is 100% deterministic - same inputs always produce same outputs.
 * No randomness, no jitter, no baseline integration.
 * 
 * @param inputs Photo metadata and warnings for each angle
 * @returns GlowScore result with score, pH estimate, and confidence
 */
export function calculateGlowScore(inputs: GlowScoreInputs): GlowScoreResult {
  const {
    frontMeta,
    frontWarnings = [],
    leftSideMeta,
    leftSideWarnings = [],
    rightSideMeta,
    rightSideWarnings = [],
    rekognitionFaceCounts,
  } = inputs;

  // Start with neutral/healthy baseline
  let skinHealthScore = 65;

  // Collect all photo metadata
  const allPhotos = [frontMeta, leftSideMeta, rightSideMeta].filter(
    (m): m is PhotoMetadata => !!m
  );
  const allWarnings = [...frontWarnings, ...leftSideWarnings, ...rightSideWarnings];
  const photoCount = allPhotos.length;

  if (photoCount === 0) {
    return {
      score: 35,
      estimatedPH: 5.8,
      confidenceLevel: 'low',
      confidenceScore: 0,
      factors: {
        photoCount: 0,
        totalFaces: 0,
        qualityWarnings: allWarnings.length,
      },
    };
  }

  // Create deterministic seed from image hashes and characteristics
  // This ensures same images = same score
  const imageHashes = allPhotos.map((p) => p.hash || '').join('|');
  const rk = rekognitionFaceCounts;
  const seedFaceParts = [
    frontMeta &&
      `${frontMeta.width || 0}x${frontMeta.height || 0}_${frontMeta.brightness ?? 0}_${effectiveFaceCountForGlowScore(frontMeta, rk?.front) ?? 0}`,
    leftSideMeta &&
      `${leftSideMeta.width || 0}x${leftSideMeta.height || 0}_${leftSideMeta.brightness ?? 0}_${effectiveFaceCountForGlowScore(leftSideMeta, rk?.leftSide) ?? 0}`,
    rightSideMeta &&
      `${rightSideMeta.width || 0}x${rightSideMeta.height || 0}_${rightSideMeta.brightness ?? 0}_${effectiveFaceCountForGlowScore(rightSideMeta, rk?.rightSide) ?? 0}`,
  ].filter(Boolean);
  const imageCharacteristics = seedFaceParts.join('|');
  const seed = `${imageHashes}_${imageCharacteristics}`;

  const usedRekognitionFaceCounts = !!(
    rk &&
    (typeof rk.front === 'number' ||
      typeof rk.leftSide === 'number' ||
      typeof rk.rightSide === 'number')
  );
  if (usedRekognitionFaceCounts) {
    console.log('[DEBUG] GlowScore: Rekognition face counts applied (preferred over FaceDetector where set)', {
      rekognitionFaceCounts: rk,
      effectiveFront: effectiveFaceCountForGlowScore(frontMeta, rk?.front),
      effectiveLeft: effectiveFaceCountForGlowScore(leftSideMeta, rk?.leftSide),
      effectiveRight: effectiveFaceCountForGlowScore(rightSideMeta, rk?.rightSide),
    });
  }

  // === AI SKIN pH ESTIMATION (Deterministic) ===
  // Calculate pH based on image characteristics - same images = same pH
  // Normal skin pH ranges 4.7-5.9 (slightly acidic)
  let estimatedPH = 5.3; // Default to middle of normal range

  // Brightness affects perceived skin health (brighter = healthier glow)
  const brightnessValues = allPhotos
    .map((p) => p.brightness)
    .filter((v): v is number => typeof v === 'number');

  if (brightnessValues.length > 0) {
    const avgBrightness =
      brightnessValues.reduce((sum, v) => sum + v, 0) / brightnessValues.length;

    // Optimal brightness (70-120) suggests healthy, well-hydrated skin
    if (avgBrightness >= 70 && avgBrightness <= 120) {
      // Use deterministic value based on image hash
      estimatedPH = 5.0 + deterministicRandom(`${seed}_ph`, 0, 0.5); // Healthy range: 5.0-5.5
      skinHealthScore += 8;
    } else if (avgBrightness >= 50 && avgBrightness < 70) {
      estimatedPH = 5.3 + deterministicRandom(`${seed}_ph`, 0, 0.7); // Slightly alkaline: 5.3-6.0
      skinHealthScore += 2;
    } else if (avgBrightness > 120 && avgBrightness <= 150) {
      estimatedPH = 5.5 + deterministicRandom(`${seed}_ph`, 0, 0.5); // Slightly alkaline: 5.5-6.0
      skinHealthScore += 1;
    } else {
      estimatedPH = 5.8 + deterministicRandom(`${seed}_ph`, 0, 0.7); // More alkaline: 5.8-6.5
      skinHealthScore -= 3;
    }
  }

  // Clamp pH to valid skin range (4.5-6.5)
  estimatedPH = clamp(estimatedPH, 4.5, 6.5);
  estimatedPH = Math.round(estimatedPH * 10) / 10; // Round to 1 decimal

  // === SKIN TEXTURE ANALYSIS (Deterministic) ===
  // Higher resolution = better texture visibility = more accurate analysis
  const resolutionValues = allPhotos
    .map((p) => (p.width && p.height ? p.width * p.height : null))
    .filter((v): v is number => typeof v === 'number');

  let avgResolution: number | undefined;
  if (resolutionValues.length > 0) {
    avgResolution =
      resolutionValues.reduce((sum, v) => sum + v, 0) / resolutionValues.length;

    // High resolution allows better texture analysis
    if (avgResolution >= 1500000) {
      // Deterministic texture score based on image characteristics
      const textureScore = 70 + deterministicRandom(`${seed}_texture`, 0, 15); // 70-85 range
      skinHealthScore += (textureScore - 70) / 10; // Add 0-1.5 points
    } else if (avgResolution >= 800000) {
      const textureScore = 60 + deterministicRandom(`${seed}_texture`, 0, 20); // 60-80 range
      skinHealthScore += (textureScore - 70) / 15; // Add -0.67 to +0.67 points
    } else {
      skinHealthScore -= 2; // Lower resolution = less accurate texture analysis
    }
  }

  // === FACE DETECTION & ANALYSIS QUALITY ===
  const faceCounts: number[] = [];
  if (frontMeta) {
    const v = effectiveFaceCountForGlowScore(frontMeta, rk?.front);
    if (typeof v === 'number') faceCounts.push(v);
  }
  if (leftSideMeta) {
    const v = effectiveFaceCountForGlowScore(leftSideMeta, rk?.leftSide);
    if (typeof v === 'number') faceCounts.push(v);
  }
  if (rightSideMeta) {
    const v = effectiveFaceCountForGlowScore(rightSideMeta, rk?.rightSide);
    if (typeof v === 'number') faceCounts.push(v);
  }
  const totalFaces = faceCounts.reduce((sum, v) => sum + v, 0);
  const photosWithFaces = faceCounts.filter((count) => count > 0).length;

  if (totalFaces === 0) {
    skinHealthScore -= 10; // Cannot analyze without faces
  } else if (totalFaces === photoCount && photoCount >= 2) {
    // Perfect detection - one face per photo
    if (photoCount === 3) {
      skinHealthScore += 6; // Comprehensive analysis with all angles
    } else {
      skinHealthScore += 3; // Good analysis with 2 photos
    }
  } else if (totalFaces < photoCount) {
    skinHealthScore -= 4; // Some photos missing faces
  } else if (totalFaces > photoCount) {
    skinHealthScore -= 5; // Multiple faces = unclear analysis
  }

  // === SKIN CONDITION INDICATORS ===
  // Warnings indicate potential skin issues (low light, multiple faces, etc.)
  // But don't penalize too harshly - these are image quality issues, not skin health
  const qualityWarnings = allWarnings.length;
  if (qualityWarnings === 0) {
    skinHealthScore += 3; // Perfect image quality
  } else if (qualityWarnings <= 2) {
    skinHealthScore -= 1; // Minor quality issues
  } else {
    skinHealthScore -= 3; // Multiple quality issues may affect accuracy
  }

  // === COMPREHENSIVE ANALYSIS BONUS ===
  if (photoCount === 3) {
    skinHealthScore += 5; // All three angles provide most accurate assessment
  } else if (photoCount === 2) {
    skinHealthScore += 2; // Two angles are good
  }

  // === DETERMINISTIC SKIN HEALTH VARIATION ===
  // Use image characteristics to add consistent variation (not random)
  // This ensures same images produce same variation
  const variation = deterministicRandom(`${seed}_variation`, -10, 10); // -10 to +10 variation
  skinHealthScore += variation;

  // === pH-BASED ADJUSTMENTS ===
  // Adjust score based on estimated pH (normal range: 4.7-5.9)
  if (estimatedPH >= 4.7 && estimatedPH <= 5.9) {
    skinHealthScore += 5; // Normal pH range
  } else if (estimatedPH > 5.9 && estimatedPH <= 6.0) {
    skinHealthScore += 1; // Slightly above normal but acceptable
  } else if (estimatedPH > 6.0) {
    skinHealthScore -= 3; // Too alkaline - may indicate dryness or barrier issues
  } else if (estimatedPH < 4.7) {
    skinHealthScore -= 2; // Too acidic - may indicate irritation
  }

  // === FINAL SCORE CALCULATION ===
  // Ensure realistic distribution across the range
  // Most users should score 50-85, with extremes at 35-98
  let finalScore = Math.round(skinHealthScore);

  // Apply realistic bounds
  finalScore = clamp(finalScore, 35, 98);

  // Ensure we don't always hit minimum - use deterministic value if too low
  if (finalScore <= 40 && photoCount >= 2) {
    finalScore = 40 + Math.round(deterministicRandom(`${seed}_lowscore`, 0, 15)); // 40-55 range for low scores
  }

  // Calculate confidence
  const avgBrightness =
    brightnessValues.length > 0
      ? brightnessValues.reduce((sum, v) => sum + v, 0) / brightnessValues.length
      : undefined;

  const confidence = calculateConfidence(
    photoCount,
    totalFaces,
    qualityWarnings,
    avgBrightness,
    avgResolution
  );

  console.log('[DEBUG] GlowScore calculated (Deterministic)', {
    baseScore: skinHealthScore,
    finalScore,
    estimatedPH: estimatedPH.toFixed(2),
    photoCount,
    totalFaces,
    warnings: qualityWarnings,
    confidence: confidence.level,
    confidenceScore: confidence.score,
    seedHash: seed.substring(0, 20) + '...',
  });

  return {
    score: finalScore,
    estimatedPH,
    confidenceLevel: confidence.level,
    confidenceScore: confidence.score,
    factors: {
      photoCount,
      totalFaces,
      qualityWarnings,
      avgBrightness,
      avgResolution,
    },
  };
}




